/**
 * PersonalPrepFieldsConfigModal Component
 *
 * Modal for editing personal prep fields configuration from the workflow screen.
 * Allows users to modify which fields are tracked (add, edit, delete, reorder).
 * Includes "Apply to future sessions" checkbox to optionally update global settings.
 *
 * Key behaviors:
 * - Manual Save/Cancel (not auto-save) - config changes are significant
 * - Checkbox always visible; default checked if draft matched global at open, unchecked if divergent
 * - "Reset to defaults" button visible when draft was divergent at modal open
 * - Alert shown when draft was divergent at modal open (not triggered by active edits)
 * - Saves to draft config first, optionally to global settings if checkbox checked
 * - Warnings (duplicate names) are non-blocking; only errors prevent save
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Checkbox,
  Chip,
  SortableList,
  DragHandle,
  EmptyState,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { sessionDraftService } from '@/services/SessionDraftService';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { computePersonalPrepHash } from '../utils/sessionDraftUtils';
import type { PreTradingConfigSnapshot } from '@/types/transient/SessionDraft';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';
import type { DropdownOption } from '@/types/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Personal prep field with required id for SortableList
 */
interface PersonalPrepField {
  id: string;
  name: string;
  type: 'checkbox' | 'text' | 'number' | 'dropdown';
  description?: string;
  options?: DropdownOption[];
}

/**
 * Blocking validation errors (prevent save)
 */
interface FieldValidationErrors {
  name?: string;
  options?: string;
}

/**
 * Non-blocking warnings (shown but don't prevent save)
 */
interface FieldValidationWarnings {
  name?: string;
}

/**
 * Field type display configuration
 */
const FIELD_TYPE_CONFIG: Record<
  PersonalPrepField['type'],
  { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' }
> = {
  checkbox: { label: 'Checkbox', color: 'success' },
  text: { label: 'Text', color: 'primary' },
  number: { label: 'Number', color: 'secondary' },
  dropdown: { label: 'Dropdown', color: 'warning' },
};

const FIELD_TYPE_OPTIONS: Array<{ key: PersonalPrepField['type']; label: string }> = [
  { key: 'checkbox', label: 'Checkbox' },
  { key: 'text', label: 'Text' },
  { key: 'number', label: 'Number' },
  { key: 'dropdown', label: 'Dropdown' },
];

export interface PersonalPrepFieldsConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Session draft ID */
  draftId: string;
  /** Current draft config snapshot */
  draftConfig: PreTradingConfigSnapshot;
  /** Current global settings (should be fresh when modal opens) */
  currentSettings: PreTradingSettings;
  /** Callback on successful save with updated config and migrated values */
  onSaveSuccess: (
    updatedConfig: PreTradingConfigSnapshot,
    migratedPersonalPrep: Record<string, boolean | string | number> | undefined
  ) => void;
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate a single field
 * Returns separate errors (blocking) and warnings (non-blocking)
 */
function validateField(
  field: PersonalPrepField,
  allFields: PersonalPrepField[]
): { errors: FieldValidationErrors; warnings: FieldValidationWarnings } {
  const errors: FieldValidationErrors = {};
  const warnings: FieldValidationWarnings = {};
  const trimmedName = field.name.trim();

  // Name required (blocking error)
  if (!trimmedName) {
    errors.name = 'Name is required';
  } else {
    // Check for duplicate names (warning only, not blocking)
    const isDuplicate = allFields.some(
      (f) =>
        f.id !== field.id &&
        f.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      warnings.name = 'Duplicate field name';
    }
  }

  // Dropdown must have at least one option (blocking error)
  if (field.type === 'dropdown') {
    if (!field.options || field.options.length === 0) {
      errors.options = 'At least one option is required';
    }
  }

  return { errors, warnings };
}

/**
 * Check if any field has blocking errors (errors only, NOT warnings)
 */
function hasBlockingErrors(
  fields: PersonalPrepField[],
  validationErrors: Record<string, FieldValidationErrors>
): boolean {
  return fields.some((field) => {
    const fieldErrors = validationErrors[field.id];
    return fieldErrors && Object.keys(fieldErrors).length > 0;
  });
}

/**
 * Compare local fields against current settings to detect divergence.
 * Used ONLY for computing wasInitiallyDivergent on modal open.
 */
function isPersonalPrepFieldsDivergent(
  localFields: PersonalPrepField[],
  currentSettings: PreTradingSettings
): boolean {
  const currentFieldsList = currentSettings.personalPrepFields;

  // Different count = divergent
  if (localFields.length !== currentFieldsList.length) {
    return true;
  }

  // Build map of current fields by id
  const currentById = new Map(currentFieldsList.map((f) => [f.id, f]));

  // Check each local field exists in current with same properties
  for (const localField of localFields) {
    const currentField = currentById.get(localField.id);
    if (!currentField) {
      return true;
    }
    if (
      localField.name !== currentField.name ||
      localField.type !== currentField.type ||
      localField.description !== currentField.description
    ) {
      return true;
    }
    // Compare options for dropdown fields
    if (localField.type === 'dropdown') {
      const localOptions = localField.options ?? [];
      const currentOptions = currentField.options ?? [];
      if (localOptions.length !== currentOptions.length) {
        return true;
      }
      const currentOptionIds = new Set(currentOptions.map((o) => o.id));
      for (const opt of localOptions) {
        if (!currentOptionIds.has(opt.id)) {
          return true;
        }
        const currentOpt = currentOptions.find((o) => o.id === opt.id);
        if (currentOpt && opt.label !== currentOpt.label) {
          return true;
        }
      }
    }
  }

  return false;
}

// ============================================================================
// Component
// ============================================================================

export const PersonalPrepFieldsConfigModal: React.FC<PersonalPrepFieldsConfigModalProps> = ({
  isOpen,
  onClose,
  draftId,
  draftConfig,
  currentSettings,
  onSaveSuccess,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localFields, setLocalFields] = useState<PersonalPrepField[]>([]);
  const [applyToFuture, setApplyToFuture] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, FieldValidationErrors>
  >({});
  const [validationWarnings, setValidationWarnings] = useState<
    Record<string, FieldValidationWarnings>
  >({});

  // Track whether draft was divergent from global when modal opened (snapshot, not live)
  const [wasInitiallyDivergent, setWasInitiallyDivergent] = useState(false);

  // Edit modal state
  const [editingFieldId, setEditingFieldId] = useState<string | 'new' | null>(null);
  const [editingDraft, setEditingDraft] = useState<PersonalPrepField | null>(null);
  const [editModalErrors, setEditModalErrors] = useState<FieldValidationErrors>({});
  const [editModalWarnings, setEditModalWarnings] = useState<FieldValidationWarnings>({});
  const [newOptionValue, setNewOptionValue] = useState('');

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Initialize local state from draftConfig when modal opens
  // Guard ensures we only initialize on open transition, not when props change while open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Deep copy fields with options
      const fieldsCopy: PersonalPrepField[] = draftConfig.personalPrepFields.map((f) => {
        const field: PersonalPrepField = {
          id: f.id,
          name: f.name,
          type: f.type,
        };
        if (f.description !== undefined) {
          field.description = f.description;
        }
        if (f.options) {
          field.options = f.options.map((o) => ({ ...o }));
        }
        return field;
      });

      setLocalFields(fieldsCopy);
      setValidationErrors({});
      setValidationWarnings({});
      setIsSaving(false);
      setEditingFieldId(null);
      setEditingDraft(null);
      setEditModalErrors({});
      setEditModalWarnings({});
      setNewOptionValue('');
      setDeleteConfirmId(null);

      // Compute initial divergence from draftConfig vs currentSettings (not local state)
      const initiallyDivergent = isPersonalPrepFieldsDivergent(
        fieldsCopy,
        currentSettings
      );
      setWasInitiallyDivergent(initiallyDivergent);

      // Checkbox default: checked if draft matched global at open, unchecked if divergent
      setApplyToFuture(!initiallyDivergent);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, draftConfig, currentSettings]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllFields = useCallback((fieldsToValidate: PersonalPrepField[]) => {
    const errors: Record<string, FieldValidationErrors> = {};
    const warnings: Record<string, FieldValidationWarnings> = {};

    fieldsToValidate.forEach((field) => {
      const result = validateField(field, fieldsToValidate);
      if (Object.keys(result.errors).length > 0) {
        errors[field.id] = result.errors;
      }
      if (Object.keys(result.warnings).length > 0) {
        warnings[field.id] = result.warnings;
      }
    });

    setValidationErrors(errors);
    setValidationWarnings(warnings);
    return errors;
  }, []);

  // ============================================================================
  // Field CRUD Handlers
  // ============================================================================

  const handleOpenAddModal = useCallback(() => {
    const newField: PersonalPrepField = {
      id: generateUUID(),
      name: '',
      type: 'checkbox',
      description: '',
      options: [],
    };
    setEditingFieldId('new');
    setEditingDraft(newField);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleOpenEditModal = useCallback((field: PersonalPrepField) => {
    setEditingFieldId(field.id);
    // Deep copy including options
    setEditingDraft({
      ...field,
      options: field.options ? field.options.map((o) => ({ ...o })) : [],
    });
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleEditDraftChange = useCallback(
    (key: keyof PersonalPrepField, value: string | DropdownOption[]) => {
      setEditingDraft((prev) => {
        if (!prev) return prev;

        let updated: PersonalPrepField;

        // Handle type change - clear options when switching away from dropdown
        if (key === 'type' && value !== 'dropdown') {
          updated = { ...prev, [key]: value as PersonalPrepField['type'], options: [] };
          setNewOptionValue(''); // Clear pending option input
        } else {
          updated = { ...prev, [key]: value };
        }

        // Validate draft
        const otherFields = editingFieldId === 'new'
          ? localFields
          : localFields.filter((f) => f.id !== editingFieldId);
        const result = validateField(updated, [...otherFields, updated]);
        setEditModalErrors(result.errors);
        setEditModalWarnings(result.warnings);

        return updated;
      });
    },
    [editingFieldId, localFields]
  );

  const handleAddOption = useCallback(() => {
    const trimmed = newOptionValue.trim();
    if (!trimmed || !editingDraft) return;

    const currentOptions = editingDraft.options ?? [];
    // Check for duplicate labels (case-insensitive)
    if (currentOptions.some((o) => o.label.toLowerCase() === trimmed.toLowerCase())) {
      return; // Don't add duplicates
    }

    const newOption: DropdownOption = { id: generateUUID(), label: trimmed };
    handleEditDraftChange('options', [...currentOptions, newOption]);
    setNewOptionValue('');
  }, [newOptionValue, editingDraft, handleEditDraftChange]);

  const handleRemoveOption = useCallback((optionId: string) => {
    if (!editingDraft) return;
    const currentOptions = editingDraft.options ?? [];
    handleEditDraftChange('options', currentOptions.filter((o) => o.id !== optionId));
  }, [editingDraft, handleEditDraftChange]);

  const handleSaveEditModal = useCallback(() => {
    if (!editingDraft) return;

    // Validate before saving
    const otherFields = editingFieldId === 'new'
      ? localFields
      : localFields.filter((f) => f.id !== editingFieldId);
    const result = validateField(editingDraft, [...otherFields, editingDraft]);

    if (Object.keys(result.errors).length > 0) {
      setEditModalErrors(result.errors);
      setEditModalWarnings(result.warnings);
      return;
    }

    // Update local fields
    let updated: PersonalPrepField[];
    if (editingFieldId === 'new') {
      updated = [...localFields, editingDraft];
    } else {
      updated = localFields.map((f) =>
        f.id === editingFieldId ? editingDraft : f
      );
    }

    setLocalFields(updated);
    validateAllFields(updated);

    // Close modal
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, [editingDraft, editingFieldId, localFields, validateAllFields]);

  const handleCancelEditModal = useCallback(() => {
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleDeleteClick = useCallback((id: string) => {
    setDeleteConfirmId(id);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteConfirmId) return;

    const updated = localFields.filter((f) => f.id !== deleteConfirmId);
    setLocalFields(updated);

    // Clear validation for deleted field
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[deleteConfirmId];
      return next;
    });
    setValidationWarnings((prev) => {
      const next = { ...prev };
      delete next[deleteConfirmId];
      return next;
    });

    // Re-validate remaining fields (duplicate warnings may clear)
    validateAllFields(updated);

    setDeleteConfirmId(null);
  }, [deleteConfirmId, localFields, validateAllFields]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  const handleReorder = useCallback(
    (reorderedFields: PersonalPrepField[]) => {
      setLocalFields(reorderedFields);
      validateAllFields(reorderedFields);
    },
    [validateAllFields]
  );

  // ============================================================================
  // Reset to Defaults
  // ============================================================================

  const handleResetToDefaults = useCallback(() => {
    // Deep copy from currentSettings
    const fieldsCopy: PersonalPrepField[] = currentSettings.personalPrepFields.map((f) => {
      const field: PersonalPrepField = {
        id: f.id,
        name: f.name,
        type: f.type,
      };
      if (f.description !== undefined) {
        field.description = f.description;
      }
      if (f.options) {
        field.options = f.options.map((o) => ({ ...o }));
      }
      return field;
    });

    setLocalFields(fieldsCopy);
    setValidationErrors({});
    setValidationWarnings({});
    // Set checkbox to checked after reset (user explicitly chose to match defaults)
    setApplyToFuture(true);
    // Clear divergence warning since user explicitly chose to match defaults
    setWasInitiallyDivergent(false);
  }, [currentSettings]);

  // ============================================================================
  // Save Handler
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Guard: currentSettings must be available
    if (!currentSettings) {
      showToast({
        title: 'Cannot save',
        description: 'Settings are not available. Please try again.',
        color: 'danger',
      });
      return;
    }

    // Validate all fields - block on errors only, not warnings
    const currentErrors = validateAllFields(localFields);
    if (hasBlockingErrors(localFields, currentErrors)) {
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Build personalPrepFieldsSettings from local state
      // Use exactOptionalPropertyTypes-safe mapping
      const personalPrepFieldsSettings: PreTradingSettings['personalPrepFields'] =
        localFields.map((f) => {
          const field: PreTradingSettings['personalPrepFields'][number] = {
            id: f.id,
            name: f.name,
            type: f.type,
          };
          if (f.description) {
            field.description = f.description;
          }
          if (f.options && f.options.length > 0) {
            field.options = f.options.map((o) => ({ ...o }));
          }
          return field;
        });

      // Step 1: Update draft config
      const draftSuccess = await sessionDraftService.updatePersonalPrepFieldsConfig(
        draftId,
        personalPrepFieldsSettings
      );

      if (!draftSuccess) {
        showToast({
          title: 'Save failed',
          description: 'Session draft not found or invalid.',
          color: 'danger',
        });
        setIsSaving(false);
        return;
      }

      // Step 2: If checkbox checked, update global settings
      let globalUpdateSucceeded = true;
      if (applyToFuture) {
        try {
          await preTradingSettingsService.update(currentSettings.id, {
            personalPrepFields: personalPrepFieldsSettings,
          });
        } catch (err) {
          console.error('Failed to update global settings:', err);
          globalUpdateSucceeded = false;
          // Flip checkbox to false so UI reflects divergence
          setApplyToFuture(false);
          showToast({
            title: 'Partial save',
            description:
              "Changes saved to current session, but couldn't update defaults for future sessions.",
            color: 'warning',
          });
        }
      }

      // Step 3: Ack hash logic
      if (applyToFuture) {
        if (globalUpdateSucceeded) {
          // Global update succeeded - hash the new global settings
          const newGlobalSettings: PreTradingSettings = {
            ...currentSettings,
            personalPrepFields: personalPrepFieldsSettings,
          };
          const ackHash = computePersonalPrepHash(newGlobalSettings);
          await sessionDraftService.setPersonalPrepAckHash(draftId, ackHash);
        }
        // If global update failed, do NOT set ack hash, keep modal open
        if (!globalUpdateSucceeded) {
          setIsSaving(false);
          return; // Keep modal open for retry
        }
      } else {
        // Checkbox unchecked - set ack hash to current global (intentional divergence)
        const ackHash = computePersonalPrepHash(currentSettings);
        await sessionDraftService.setPersonalPrepAckHash(draftId, ackHash);
      }

      // Step 4: Re-fetch updated draft to get migrated values
      const updatedDraft = await sessionDraftService.getById(draftId);
      if (updatedDraft && updatedDraft.preTradingConfig) {
        onSaveSuccess(
          updatedDraft.preTradingConfig,
          updatedDraft.personalPrep
        );
      }

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentSettings,
    localFields,
    applyToFuture,
    draftId,
    validateAllFields,
    showToast,
    onSaveSuccess,
    onClose,
  ]);

  // ============================================================================
  // Field Row Renderer
  // ============================================================================

  const renderFieldRow = useCallback(
    (field: PersonalPrepField, dragHandleProps: DragHandleProps) => {
      const typeConfig = FIELD_TYPE_CONFIG[field.type];
      const fieldErrors = validationErrors[field.id];
      const fieldWarnings = validationWarnings[field.id];
      const hasError = fieldErrors && Object.keys(fieldErrors).length > 0;
      const hasWarning = fieldWarnings && Object.keys(fieldWarnings).length > 0;

      return (
        <div
          className={`
            rounded-lg bg-default-50 p-3
            ${dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
            ${hasError ? 'ring-1 ring-danger-300' : hasWarning ? 'ring-1 ring-warning-300' : ''}
          `}
        >
          <div className="flex items-center gap-3">
            <DragHandle dragHandleProps={dragHandleProps} />

            {/* Tappable content area */}
            <button
              type="button"
              className="flex-1 text-left min-w-0"
              onClick={() => handleOpenEditModal(field)}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium truncate ${hasError ? 'text-danger' : ''}`}>
                  {field.name || 'Unnamed field'}
                </span>
                {hasError && <span className="text-danger text-xs">*</span>}
                {hasWarning && !hasError && <span className="text-warning text-xs">!</span>}
              </div>
              {field.description && (
                <div className="text-sm text-default-500 truncate">
                  {field.description}
                </div>
              )}
            </button>

            <Chip size="sm" color={typeConfig.color} variant="flat">
              {typeConfig.label}
            </Chip>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleOpenEditModal(field)}
              aria-label={`Edit ${field.name || 'field'}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteClick(field.id)}
              aria-label={`Delete ${field.name || 'field'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      );
    },
    [validationErrors, validationWarnings, handleOpenEditModal, handleDeleteClick]
  );

  // Get field name for delete confirmation
  const deleteFieldName = deleteConfirmId
    ? localFields.find((f) => f.id === deleteConfirmId)?.name || 'this field'
    : '';

  return (
    <>
      {/* Main Config Modal */}
      <Modal
        isOpen={isOpen && editingFieldId === null && deleteConfirmId === null}
        onClose={onClose}
        placement="top"
        scrollBehavior="inside"
        size="2xl"
        isDismissable={!isSaving}
        hideCloseButton={isSaving}
      >
        <ModalContent>
          <ModalHeader>
            <span className="text-lg font-semibold">Configure Personal Prep Fields</span>
          </ModalHeader>
          <ModalBody className="gap-4">
            {/* Divergent State Warning - shows if draft was divergent when modal opened */}
            {wasInitiallyDivergent && (
              <Alert
                color="warning"
                title="This draft differs from your defaults"
                description="Your current edits won't affect this warning."
                endContent={
                  <Button
                    size="sm"
                    variant="flat"
                    color="warning"
                    onPress={handleResetToDefaults}
                  >
                    Reset to defaults
                  </Button>
                }
              />
            )}

            {/* Fields List */}
            {localFields.length > 0 ? (
              <SortableList
                items={localFields}
                onReorder={handleReorder}
                renderItem={renderFieldRow}
                className="space-y-2"
              />
            ) : (
              <EmptyState
                title="No fields configured"
                description="Add fields to customize your daily pre-trading preparation."
              />
            )}

            {/* Add Field Button */}
            <Button
              variant="flat"
              color="primary"
              startContent={<Plus className="w-4 h-4" />}
              onPress={handleOpenAddModal}
              className="w-full md:w-auto"
            >
              Add Field
            </Button>

            {/* Apply to Future Sessions Checkbox - always visible */}
            <Checkbox
              isSelected={applyToFuture}
              onValueChange={setApplyToFuture}
              size="sm"
            >
              Apply to future sessions
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSave} isLoading={isSaving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit/Add Field Modal */}
      <Modal
        isOpen={editingFieldId !== null}
        onClose={handleCancelEditModal}
        placement="top"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingFieldId === 'new' ? 'Add Field' : 'Edit Field'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {editingDraft && (
              <>
                {/* Field Name */}
                <Input
                  label="Field Name"
                  placeholder="e.g., Morning Routine Complete"
                  value={editingDraft.name}
                  onChange={(e) => handleEditDraftChange('name', e.target.value)}
                  isInvalid={!!editModalErrors.name}
                  errorMessage={editModalErrors.name}
                  description={editModalWarnings.name ? (
                    <span className="text-warning">{editModalWarnings.name}</span>
                  ) : undefined}
                  autoFocus
                  classNames={{ input: 'text-base' }}
                />

                {/* Description (optional) */}
                <Input
                  label="Description (optional)"
                  placeholder="Brief description of this field"
                  value={editingDraft.description || ''}
                  onChange={(e) => handleEditDraftChange('description', e.target.value)}
                  classNames={{ input: 'text-base' }}
                />

                {/* Field Type */}
                <Select
                  label="Field Type"
                  selectedKeys={new Set([editingDraft.type])}
                  onSelectionChange={(keys) => {
                    if (keys !== 'all') {
                      const selected = Array.from(keys)[0] as PersonalPrepField['type'];
                      if (selected) {
                        handleEditDraftChange('type', selected);
                      }
                    }
                  }}
                >
                  {FIELD_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>

                {/* Dropdown Options Editor */}
                {editingDraft.type === 'dropdown' && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium">
                      Dropdown Options
                      {editModalErrors.options && (
                        <span className="text-danger ml-2 font-normal">
                          {editModalErrors.options}
                        </span>
                      )}
                    </div>

                    {/* Existing Options */}
                    {editingDraft.options && editingDraft.options.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {editingDraft.options.map((option) => (
                          <Chip
                            key={option.id}
                            variant="flat"
                            onClose={() => handleRemoveOption(option.id)}
                          >
                            {option.label}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {/* Add New Option */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="New option"
                        value={newOptionValue}
                        onChange={(e) => setNewOptionValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        size="sm"
                        classNames={{ base: 'flex-1' }}
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={handleAddOption}
                        isDisabled={!newOptionValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelEditModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSaveEditModal}
              isDisabled={Object.keys(editModalErrors).length > 0}
            >
              {editingFieldId === 'new' ? 'Add' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmId !== null} onClose={handleCancelDelete}>
        <ModalContent>
          <ModalHeader>Delete Field?</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete <strong>{deleteFieldName}</strong>?
            </p>
            <p className="text-default-500 text-sm mt-2">
              This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelDelete}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleConfirmDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PersonalPrepFieldsConfigModal;
